import { getApiClient } from "./apiClient";

export interface ProfileUpdateData {
  first_name?: string;
  last_name?: string;
  job_title?: string;
  department?: string;
  phone?: string;
}

export interface ChangePasswordData {
  current_password: string;
  new_password: string;
  new_password_confirm: string;
}

export interface ChangePasswordResponse {
  message: string;
  token: string;
}

export interface OrganizationMember {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_admin: boolean;
  is_owner: boolean;
  account_status: string;
  date_joined: string;
}

export interface PendingInvitation {
  id: number;
  guid: string;
  email: string;
  invited_by_email: string;
  invited_by_name: string;
  created: string;
}

export interface OrganizationDetail {
  id: number;
  name: string;
  slug: string;
  members: OrganizationMember[];
  member_count: number;
  is_admin: boolean;
  is_owner: boolean;
  max_users: number | null;
  pending_invite_count: number;
  pending_invitations: PendingInvitation[];
  can_add_members: boolean;
}

class SettingsApi {
  async updateProfile(data: ProfileUpdateData): Promise<any> {
    const client = await getApiClient();
    const response = await client.patch("/auth/profile/", data);
    return response.data;
  }

  async changePassword(
    data: ChangePasswordData,
  ): Promise<ChangePasswordResponse> {
    const client = await getApiClient();
    const response = await client.post("/auth/change-password/", data);
    return response.data;
  }

  async getOrganization(): Promise<OrganizationDetail> {
    const client = await getApiClient();
    const response = await client.get("/auth/organization/");
    return response.data;
  }

  async renameOrganization(name: string): Promise<OrganizationDetail> {
    const client = await getApiClient();
    const response = await client.patch("/auth/organization/", { name });
    return response.data;
  }

  async inviteMember(email: string): Promise<{ message: string }> {
    const client = await getApiClient();
    const response = await client.post("/auth/organization/invite/", {
      email,
    });
    return response.data;
  }

  // NOTE: Member removal disabled — use toggleMemberStatus instead.
  // async removeMember(userId: number): Promise<void> {
  //   const client = await getApiClient();
  //   await client.delete(`/auth/organization/members/${userId}/`);
  // }

  async toggleMemberStatus(userId: number): Promise<{ message: string }> {
    const client = await getApiClient();
    const response = await client.post(
      `/auth/organization/members/${userId}/toggle-status/`,
    );
    return response.data;
  }

  async changeMemberRole(
    userId: number,
    isAdmin: boolean,
  ): Promise<{ message: string }> {
    const client = await getApiClient();
    const response = await client.patch(
      `/auth/organization/members/${userId}/`,
      { is_admin: isAdmin },
    );
    return response.data;
  }

  async cancelInvitation(inviteId: number): Promise<void> {
    const client = await getApiClient();
    await client.delete(`/auth/organization/invitations/${inviteId}/`);
  }
}

export const settingsApi = new SettingsApi();
